import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, LayerGroup } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/statuts";
import { useStatuts } from "@/context/StatutsContext";
import { StatutBadge } from "@/components/StatutBadge";
import { Users2, MapPin } from "lucide-react";

const techIcon = (label) => L.divIcon({
  className: "",
  html: `<div style="background:#0A0A0A;color:#fff;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"><span style="transform:rotate(45deg);font-size:11px;font-weight:700">${label}</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28],
});

export default function Carte() {
  const { user } = useAuth();
  const { statuts } = useStatuts();
  const navigate = useNavigate();
  const [visites, setVisites] = useState([]);
  const [techPos, setTechPos] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({ technicien_id: "", statut: "" });
  const [showVisites, setShowVisites] = useState(true);
  const [showTechs, setShowTechs] = useState(true);
  const isInterne = ["admin", "planification"].includes(user.role);

  useEffect(() => {
    const p = new URLSearchParams();
    if (filters.technicien_id) p.set("technicien_id", filters.technicien_id);
    if (filters.statut) p.set("statut", filters.statut);
    api.get(`/visites/map?${p}`).then((r) => setVisites(r.data));
  }, [filters]);
  useEffect(() => {
    if (isInterne) {
      api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {});
      api.get("/techniciens/positions").then((r) => setTechPos(r.data)).catch(() => {});
    }
  }, [isInterne]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Carte</h1>
          <p className="text-sm text-slate-500">{visites.length} visite{visites.length > 1 ? "s" : ""}{isInterne ? ` · ${techPos.length} technicien${techPos.length > 1 ? "s" : ""} localisé${techPos.length > 1 ? "s" : ""}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isInterne && (
            <>
              <button data-testid="toggle-visites" onClick={() => setShowVisites((s) => !s)} className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${showVisites ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}><MapPin className="h-4 w-4" /> Visites</button>
              <button data-testid="toggle-techs" onClick={() => setShowTechs((s) => !s)} className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${showTechs ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500"}`}><Users2 className="h-4 w-4" /> Techniciens</button>
              <select value={filters.technicien_id} onChange={(e) => setFilters({ ...filters, technicien_id: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Tous les techniciens</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
              </select>
              <select value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Tous statuts</option>
                {statuts.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200" style={{ height: "74vh" }} data-testid="map">
        <MapContainer center={[45.75, 4.85]} zoom={11} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {showVisites && (
            <LayerGroup>
              {visites.map((v) => (
                <CircleMarker key={v.id} center={[v.latitude, v.longitude]} radius={8} pathOptions={{ color: "#fff", weight: 2, fillColor: v.statut_color, fillOpacity: 0.9 }}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold">{v.client_nom}</div>
                      <div className="text-xs text-slate-500">{v.adresse}, {v.code_postal} {v.ville}</div>
                      <div className="text-xs">{v.date_visite ? formatDate(v.date_visite) : "Non planifiée"} {v.heure_debut || ""}</div>
                      <div className="text-xs">Tech : {v.technicien_nom || "—"}</div>
                      <StatutBadge code={v.statut} color={v.statut_color} label={v.statut_label} />
                      <button onClick={() => navigate(`/visites/${v.id}`)} className="mt-1 block text-xs font-semibold text-blue-600 underline">Ouvrir la fiche</button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          )}
          {isInterne && showTechs && (
            <LayerGroup>
              {techPos.map((t) => (
                <Marker key={t.id} position={[t.latitude, t.longitude]} icon={techIcon((t.prenom[0] || "") + (t.nom[0] || ""))}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold">{t.prenom} {t.nom}</div>
                      <div className="text-xs text-slate-500">Technicien · {t.ville}</div>
                      <div className="text-xs"><b>{t.disponibilite}</b></div>
                      {t.prochain_rdv ? (
                        <div className="text-xs">Prochain RDV : {t.prochain_rdv.heure} — {t.prochain_rdv.ville}{t.prochain_rdv.distance_km != null ? ` (${t.prochain_rdv.distance_km} km)` : ""}</div>
                      ) : <div className="text-xs text-slate-400">Aucun RDV à venir</div>}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </LayerGroup>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
